import { CopyIcon } from '@approvalmax/ui';
import { Box, Button, Dropdown, Flex, Grid, Label, Progress, Text, toast } from '@approvalmax/ui/src/components';
import { InfoIcon } from 'modules/sprites';
import { memo, useCallback } from 'react';
import { useCopyToClipboard } from 'react-use';
import { ApiAmaxPayPaymentDetailsVerificationStatus } from 'shared/data/webApp/v2';

import { VerifyStatus } from '../VerifyStatus/VerifyStatus';
import { getBackgroundColorByStatus, getHintText } from './CheckConfirmationOfPayee.helpers';
import { messages } from './CheckConfirmationOfPayee.messages';
import { CheckConfirmationOfPayeeProps } from './CheckConfirmationOfPayee.types';

export const CheckConfirmationOfPayee = memo<CheckConfirmationOfPayeeProps>((props) => {
    const { progress, status, reasonCode, isError, showInfoIcon, providedName, suggestedName } = props;

    const [, setCopiedText] = useCopyToClipboard();

    const handleSuggestedNameCopy = useCallback(() => {
        if (suggestedName) {
            setCopiedText(suggestedName);

            toast.success(messages.copiedToClipboard);
        }
    }, [suggestedName, setCopiedText]);

    const hint = getHintText(reasonCode);

    return (
        <Box color={getBackgroundColorByStatus(status)} radius='xsmall' spacing='12' width='max-content'>
            <Grid
                alignItems='center'
                gridTemplateColumns={status && showInfoIcon ? 'max-content max-content auto' : 'max-content auto'}
                columnGap={4}
            >
                {hint && showInfoIcon && (
                    <Dropdown
                        activator={<InfoIcon color='midnight60' cursor='pointer' size={12} />}
                        display='flex'
                        width={310}
                    >
                        <Box spacing='16'>
                            <Grid rowGap={16}>
                                <Text font='body' fontSize='medium' whiteSpace='pre-wrap'>
                                    {getHintText(reasonCode)}
                                </Text>

                                {suggestedName && status !== ApiAmaxPayPaymentDetailsVerificationStatus.full_match && (
                                    <>
                                        <div>
                                            <Label color='midnight100' size='medium'>
                                                {messages.providedBeneficiaryDetails}
                                            </Label>

                                            <Flex alignItems='center' spacing='8'>
                                                <Text color='midnight70' font='label' fontSize='medium'>
                                                    {messages.name}
                                                </Text>

                                                <Text font='label' fontSize='medium'>
                                                    {providedName}
                                                </Text>
                                            </Flex>
                                        </div>

                                        <div>
                                            <Label color='midnight100' size='medium'>
                                                {messages.suggestedBeneficiaryDetails}
                                            </Label>

                                            <Flex alignItems='center' spacing='8'>
                                                <Text color='midnight70' font='label' fontSize='medium'>
                                                    {messages.name}
                                                </Text>

                                                <Text font='label' fontSize='medium'>
                                                    {suggestedName}
                                                </Text>

                                                <Button icon noPadding onClick={handleSuggestedNameCopy}>
                                                    <CopyIcon size={16} />
                                                </Button>
                                            </Flex>
                                        </div>
                                    </>
                                )}
                            </Grid>
                        </Box>
                    </Dropdown>
                )}

                <Text color='midnight60' font='label' fontSize='small'>
                    {messages.confirmationOfPayee}
                </Text>

                {progress ? (
                    <Grid alignItems='center' gridTemplateColumns='max-content auto' columnGap={4}>
                        <Progress shape='circle' size='uxsmall' />

                        <Text color='midnight60' font='label' fontSize='small'>
                            {messages.calculating}
                        </Text>
                    </Grid>
                ) : (
                    <VerifyStatus status={status} isError={isError} />
                )}
            </Grid>
        </Box>
    );
});

CheckConfirmationOfPayee.displayName = 'CheckConfirmationOfPayee';
